const API_BASE = import.meta.env.VITE_API_BASE ?? 'https://api.dev.stella-cars.com/api';

export class ApiAuthError extends Error {
  constructor() { super('UNAUTHORIZED'); this.name = 'ApiAuthError'; }
}

/**
 * Renvoie l'expertId actif pour la vue (impersonation admin). Lu directement
 * dans localStorage pour ne pas dependre d'un hook React.
 */
function currentViewAsExpert(): string | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage.getItem('stella-view-as-expert') : null;
  } catch {
    return null;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  });
  // On NE redirige PAS vers /login sur 401 : sinon boucle infinie quand l'API
  // n'accepte pas encore l'auth SWA. On laisse la query gérer l'erreur proprement.
  if (res.status === 401) {
    throw new ApiAuthError();
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`API ${res.status}: ${text || res.statusText}`);
  }
  return res.json() as Promise<T>;
}

export type LeadStatut =
  | 'nouveau'
  | 'qualifié'
  | 'rdv_pris'
  | 'relance'
  | 'relance_1'
  | 'relance_2'
  | 'signe'
  | 'gagné'
  | 'perdu'
  | 'archivé';

export interface DetailPrix {
  cote: number;
  decoteKm: number;
  fraisRemiseEnEtat: number;
  margeStella: number;
  total: number;
}

export interface Estimation {
  prixRachat: number;
  prixRevente: number;
  marge: number;
  pointsForts: string[];
  pointsFaibles: string[];
  detailPrix?: DetailPrix;
}

export interface RelanceLog {
  num: 1 | 2;
  sentAt: string;
  channel: 'email' | 'sms';
  messageId?: string;
}

export interface Lead {
  id: string;
  client: { nom: string; prenom: string; email: string; tel: string };
  vehicule: {
    immat: string;
    marque?: string;
    modele?: string;
    finition?: string;
    km: number;
    dateMec?: string;
    energie?: string;
    puissance?: number;
    boite?: string;
    carrosserie?: string;
    couleur?: string;
    vin?: string;
    prixCatalogueNeuf?: number;
  };
  estimation: Estimation;
  statut: LeadStatut;
  expert?: string;
  createdAt: string;
  notes?: string;
  relances?: RelanceLog[];
  unsubscribed?: boolean;
  unsubscribedAt?: string;
  rdv?: { date: string; expertId: string; statut?: string; graphEventId?: string };
}

export interface Rdv {
  id: string;
  leadId: string;
  source: 'leboncoin' | 'lacentrale' | 'autobiz' | 'autoscout24' | 'manuel' | 'web';
  client: { nom: string; prenom?: string; email?: string; tel: string };
  vehicule: string;
  vehiculeDetails?: {
    immat?: string;
    marque?: string;
    modele?: string;
    finition?: string;
    km?: number;
    dateMec?: string;
    energie?: string;
    puissance?: number;
    boite?: string;
    carrosserie?: string;
    couleur?: string;
    vin?: string;
    prixCatalogueNeuf?: number;
  };
  estimation?: Estimation;
  start: string;
  end: string;
  expert: string;
  expertId?: string;
  statut: 'planifié' | 'confirmé' | 'honoré' | 'no_show' | 'annulé';
  conflit?: boolean;
  graphEventId?: string;
  createdAt?: string;
  notes?: string;
  briefIa?: BriefIa;
}

export interface BriefIa {
  pointsAValoriser: string[];
  objections: { question: string; reponse: string }[];
  margeNegociation: {
    plancher: number;
    cible: number;
    max: number;
    commentaire: string;
  };
  questionsAPoser: string[];
  generatedAt: string;
  model: string;
}

// Payloads pour modifier/creer un RDV depuis le BO (recette client 26-06-26).
export interface RdvPatchPayload {
  start?: string;
  end?: string;
  expertId?: string;
  statut?: 'planifie' | 'confirme' | 'honore' | 'no_show' | 'annule';
  notes?: string;
}

export interface RdvCreatePayload {
  expertId: string;
  start: string;
  end?: string;
  client?: { nom?: string; prenom?: string; email?: string; tel?: string };
  vehicule?: string;
  vehiculeImmat?: string;
  notes?: string;
  source?: string;
}

export interface DashboardKpi {
  leadsJour: number;
  leadsSemaine: number;
  rdvJour: number;
  rdvSemaine: number;
  estimationsMois: number;
  rdvPrisMois: number;
  gagnesMois: number;
  perdusMois: number;
  enCoursMois: number;
  tauxConversion: number;
  tauxEstimVersRdv: number;
  tauxRdvVersAchat: number;
  caTotalMois: number;
  prixVenteMoyen: number;
  margeMoyenne: number;
  noShowRate: number;
  evolution14j: { day: string; estimations: number; rdv: number; gagnes: number }[];
  syncStatus: { source: string; lastSync: string; ok: boolean }[];
  // Stats détaillées (recette client 15/06/26).
  expertStats: ExpertStat[];
  sourceStats: SourceStat[];
}

export interface ExpertStat {
  expertId: string;
  rdvPris: number;
  achats: number;
  perdus: number;
  taux: number;
  ca: number;
}

export interface SourceStat {
  source: string;
  rdvCount: number;
  pct: number;
}

export const api = {
  dashboard: () => request<DashboardKpi>('/dashboard'),
  leadsList: (params?: { statut?: LeadStatut; q?: string; offset?: number; limit?: number }) => {
    const qs = new URLSearchParams();
    if (params?.statut) qs.set('statut', params.statut);
    if (params?.q) qs.set('q', params.q);
    if (params?.offset !== undefined) qs.set('offset', String(params.offset));
    if (params?.limit !== undefined) qs.set('limit', String(params.limit));
    const s = qs.toString();
    return request<{ items: Lead[]; total: number; offset: number; limit: number }>(`/leads${s ? `?${s}` : ''}`);
  },
  leadGet: (id: string) => request<Lead>(`/leads/${id}`),
  leadClose: (id: string, body: { issue: 'gagné' | 'perdu'; motif?: string; prixVente?: number }) =>
    request<Lead>(`/leads/${id}/close`, { method: 'POST', body: JSON.stringify(body) }),
  leadRelance: (id: string) =>
    request<{ ok: true }>(`/leads/${id}/relance`, { method: 'POST' }),
  rdvList: (params?: { from?: string; to?: string; expert?: string }) => {
    const qs = new URLSearchParams();
    if (params?.from) qs.set('from', params.from);
    if (params?.to) qs.set('to', params.to);
    // Filtre expert : param explicite > view-as (admin)
    const exp = params?.expert ?? currentViewAsExpert() ?? '';
    if (exp) qs.set('expert', exp);
    const s = qs.toString();
    return request<{ items: Rdv[] }>(`/rdv${s ? `?${s}` : ''}`);
  },
  rdvGet: (id: string) => request<Rdv>(`/rdv/${id}`),
  rdvReconcile: () => request<{ resolved: number }>(`/rdv/reconcile`, { method: 'POST' }),
  rdvBriefRegenerate: (id: string) =>
    request<{ briefIa: BriefIa }>(`/rdv/${id}/brief`, { method: 'POST' }),
  rdvPatch: (id: string, body: RdvPatchPayload) =>
    request<{ ok: true; id: string }>(`/rdv/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  rdvDelete: (id: string) =>
    request<{ ok: true; id: string }>(`/rdv/${id}`, { method: 'DELETE' }),
  rdvCreate: (body: RdvCreatePayload) =>
    request<{ ok: true; id: string }>(`/rdv`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  expertsList: () => request<{ items: Expert[] }>(`/experts`),
  expertBlocksList: (params?: { expertId?: string; from?: string; to?: string }) => {
    const qs = new URLSearchParams();
    if (params?.expertId) qs.set('expertId', params.expertId);
    if (params?.from) qs.set('from', params.from);
    if (params?.to) qs.set('to', params.to);
    const s = qs.toString();
    return request<{ items: ExpertBlock[]; from: string; to: string }>(
      `/expert-blocks${s ? `?${s}` : ''}`,
    );
  },
  expertBlockCreate: (body: { expertId: string; startIso: string; endIso: string; reason?: string }) =>
    request<ExpertBlock>(`/expert-blocks`, { method: 'POST', body: JSON.stringify(body) }),
  expertBlockDelete: (id: string, expertId: string) =>
    request<void>(`/expert-blocks/${encodeURIComponent(id)}?expertId=${encodeURIComponent(expertId)}`, {
      method: 'DELETE',
    }),
  // Fermetures exceptionnelles de la concession (vacances / jours feries).
  // `actor` = UPN de l'utilisateur connecte (header x-stella-actor) : requis
  // par l'API pour l'autorisation (Marco + admins) tant que le BO appelle
  // l'API en cross-origin (le principal SWA n'est pas propage).
  closuresList: (params?: { from?: string; to?: string }) => {
    const qs = new URLSearchParams();
    if (params?.from) qs.set('from', params.from);
    if (params?.to) qs.set('to', params.to);
    const s = qs.toString();
    return request<{ items: Closure[]; from: string; to: string }>(`/closures${s ? `?${s}` : ''}`);
  },
  closureCreate: (body: { startIso: string; endIso: string; reason?: string }, actor: string) =>
    request<Closure>(`/closures`, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: { 'x-stella-actor': actor },
    }),
  closureDelete: (id: string, actor: string) =>
    request<void>(`/closures/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      headers: { 'x-stella-actor': actor },
    }),
  supervision: () => request<SupervisionPayload>(`/supervision`),
};

export interface Expert {
  id: string;
  name: string;
  shortName: string;
  mailbox: string;
  specialty: string;
}

export interface ExpertBlock {
  id: string;
  expertId: string;
  startIso: string;
  endIso: string;
  reason?: string;
  createdAt: string;
  createdBy?: string;
}

/** Fermeture exceptionnelle de la concession (tous les experts). */
export interface Closure {
  id: string;
  startIso: string;
  endIso: string;
  reason?: string;
  createdAt: string;
  createdBy?: string;
}

export interface SupervisionPayload {
  generatedAt: string;
  centraleSync: {
    status: 'ok' | 'warning' | 'error' | 'unknown';
    lastRunAt?: string;
    minutesSinceLastRun?: number;
    nextRunInMin?: number;
    scanned: number;
    created: number;
    updated: number;
    cancelled: number;
    skipped: number;
    errors: number;
    markedRead: number;
    byExpert: Record<string, number>;
    errorMessages: { subject?: string; error?: string }[];
  };
  planningInbox: {
    status: 'ok' | 'error';
    unreadCount?: number;
    totalCount?: number;
    error?: string;
  };
  expertCalendars: Array<{
    id: string;
    shortName: string;
    mailbox: string;
    status: 'ok' | 'error';
    eventsCount30j?: number;
    nextEventAt?: string;
    error?: string;
  }>;
  cosmos: {
    status: 'ok' | 'error';
    appointmentsTotal?: number;
    leadsTotal?: number;
    blocksActive?: number;
    error?: string;
  };
  dispatch30d: Record<string, number>;
  traffic: {
    estimations7d: number;
    estimations30d: number;
    evolution14d: { day: string; estimations: number }[];
  };
  weeklyLoad: {
    weekStartIso: string;
    weekEndIso: string;
    days: { date: string; label: string; perExpert: Record<string, number>; total: number }[];
    totalsByExpert: Record<string, number>;
  };
  recentErrors: Array<{
    id: string;
    action?: string;
    createdAt?: string;
    error?: any;
    message?: string;
    stage?: string;
    op?: string;
  }>;
}
